<section
  class="lifecycle-actions"
  [class.lifecycle-actions--embedded]="publicationLayout === 'embedded' && draft"
  [attr.aria-labelledby]="
    (publicationLayout === 'embedded' && draft) ||
    (managedVersionActions && version && !draft)
      ? null
      : 'workflow-lifecycle-actions-title'
  "
  [attr.aria-label]="
    managedVersionActions && version && !draft
      ? 'Availability actions'
      : publicationLayout === 'embedded' && draft
        ? 'Publication details'
        : null
  "
>
  <header
    *ngIf="
      (publicationLayout !== 'embedded' || !draft) &&
      !(managedVersionActions && version && !draft)
    "
    class="lifecycle-actions__header"
  >
    <div>
      <p class="lifecycle-actions__eyebrow">
        {{ draft ? "Publication" : "Make this workflow available" }}
      </p>
      <h4 id="workflow-lifecycle-actions-title" tabindex="-1">
        {{
          draft
            ? "Publish " + publicationName()
            : "Availability of " + versionName()
        }}
      </h4>
    </div>
    <span>{{
      draft
        ? "Revision " + draft.revision
        : "Version " + version?.definitionVersionCode
    }}</span>
  </header>

  <p
    *ngIf="!draft && !(managedVersionActions && version)"
    class="lifecycle-actions__assurance-note"
  >
    Choose availability for new process runs below. Existing runs keep their
    current version.
  </p>

  <div
    *ngIf="statusMessage()"
    class="lifecycle-actions__status"
    role="status"
    aria-live="polite"
  >
    <i class="pi pi-info-circle" aria-hidden="true"></i>
    <span>{{ statusMessage() }}</span>
  </div>

  <div
    *ngIf="errorTitle()"
    class="lifecycle-actions__error"
    role="alert"
    tabindex="-1"
  >
    <strong>{{ errorTitle() }}</strong>
    <ul>
      <li *ngFor="let message of errorMessages()">{{ message }}</li>
    </ul>
    <p *ngIf="errorReference()">
      Support reference: <code>{{ errorReference() }}</code>
    </p>
  </div>

  <ng-container *ngIf="draft as currentDraft">
    <p
      *ngIf="!canPublish() && publicationLayout !== 'embedded'"
      class="lifecycle-actions__permission-note"
    >
      You can inspect readiness. Publishing controls are shown only to users
      with the workflow publisher permission.
    </p>

    <div
      *ngIf="showPublicationReadiness && publishBlockedReason() && !isBusy()"
      class="lifecycle-actions__publication-gate"
      role="status"
      aria-live="polite"
    >
      <i class="pi pi-lock" aria-hidden="true"></i>
      <div>
        <strong>{{ publicationReadiness().label }}</strong>
        <p>{{ publicationReadiness().detail }}</p>
      </div>
    </div>

    <aside
      *ngIf="
        showPublicationReadiness &&
        draftExecutionSupport().status === 'attention'
      "
      class="lifecycle-actions__activation-notice"
      aria-label="Activation and process runs"
    >
      <i class="pi pi-info-circle" aria-hidden="true"></i>
      <div>
        <strong>{{ draftExecutionSupport().label }}</strong>
        <p>{{ draftExecutionSupport().detail }}</p>
      </div>
    </aside>

    <form
      *ngIf="showPublicationForm(); else publicationExplanation"
      class="lifecycle-actions__form"
      (ngSubmit)="publishDraft()"
    >
      <fieldset
        class="lifecycle-actions__fields lifecycle-actions__fields--publish"
        [disabled]="
          isBusy() ||
          isConfirming() ||
          isCheckingPublication() ||
          interactionBlocked
        "
      >
        <legend class="visually-hidden">Publication details</legend>
        <label>
          <span>Publication note <small>Optional</small></span>
          <input
            uiControl
            type="text"
            name="publishReasonCode"
            maxlength="120"
            autocomplete="off"
            placeholder="For example: Ready for the team to test"
            aria-describedby="workflow-publication-reason-hint"
            [(ngModel)]="publishReasonCode"
          />
          <small id="workflow-publication-reason-hint"
            >Add a short note for the history, in your own words. No code is
            needed.</small
          >
        </label>
        <details
          class="lifecycle-actions__publication-details"
          [open]="!!publishDisplayName"
        >
          <summary>Use a different published name</summary>
          <label>
            <span>Published name <small>Optional</small></span>
            <input
              uiControl
              type="text"
              name="publishDisplayName"
              maxlength="150"
              autocomplete="off"
              [placeholder]="
                currentDraft.document.displayName || 'Keep the workflow name'
              "
              [(ngModel)]="publishDisplayName"
            />
          </label>
        </details>
      </fieldset>
      <p class="lifecycle-actions__publication-effect">
        Publish saves version {{ currentDraft.document.definitionVersionCode }}.
        Then activate it to allow new process runs. To make changes later,
        create a new draft. Existing runs keep their current version.
      </p>
      <ui-button
        *ngIf="publicationLayout !== 'embedded'"
        buttonType="submit"
        class="lifecycle-actions__primary"
        severity="primary"
        pIcon="pi-send"
        [buttonText]="publishActionLabel()"
        [disabled]="!canSubmitPublish()"
      />
    </form>
    <ng-template #publicationExplanation>
      <ng-container *ngIf="publicationLayout !== 'embedded'">
        <p
          *ngIf="!showPublicationReadiness"
          class="lifecycle-actions__permission-note"
        >
          Resolve the publication check above to enable final review.
        </p>
        <details class="lifecycle-actions__publication-details">
          <summary>What happens when this revision is published?</summary>
          <p>
            Publish saves version
            {{ currentDraft.document.definitionVersionCode }}. Then activate it to
            allow new process runs. To make changes later, create a new draft.
            Existing runs keep their current version.
          </p>
          <p *ngIf="canPublish()">
            Publication details become available when the checks above are
            complete.
            <span *ngIf="publishDisplayName || publishReasonCode"
              >Your entered name and note have been kept.</span
            >
          </p>
        </details>
      </ng-container>
    </ng-template>
  </ng-container>

  <ng-container *ngIf="version as currentVersion">
    @let view = versionView;
    @let preview = versionChange();
    @let checkedPreview = preview && preview.definitionVersionId === currentVersion.id && !versionChangeFailed() && !isLoadingVersionChange() ? preview : null;
    <p *ngIf="isLoadingVersionChange()" role="status">
      Checking the current workflow version…
    </p>
    <p *ngIf="versionChangeFailed()" role="alert">
      Current availability could not be checked.
      <button
        uiButton
        type="button"
        [disabled]="isBusy() || isConfirming()"
        (click)="retryVersionChangePreview(view)"
      >
        Retry availability checks
      </button>
    </p>
    <p
      *ngIf="!hasAnyVersionPermission()"
      class="lifecycle-actions__permission-note"
    >
      You can inspect this version and its activation history. Your permissions
      do not allow activation, rollback or retirement.
    </p>

    <section
      *ngIf="versionReview as review"
      class="lifecycle-actions__availability"
      aria-labelledby="workflow-current-availability-title"
      data-current-availability
      [attr.data-tone]="review.availability.tone"
    >
      <h5 id="workflow-current-availability-title" tabindex="-1">
        {{ checkedPreview ? "Current availability" : "Last reported availability" }}
      </h5>
      <strong
        class="lifecycle-actions__availability-answer"
        data-testid="workflow-version-availability"
        >{{ review.availability.answer }}</strong
      >
      <p>{{ review.availability.detail }}</p>
      <p *ngIf="!checkedPreview" class="lifecycle-actions__permission-note">
        The current selection has not been confirmed. This is the last reported
        availability for this version.
      </p>
      <ng-container *ngIf="checkedPreview as currentPreview">
        <div class="lifecycle-actions__selection" data-current-version-selection>
          <p>
            Current selection:
            <strong>{{
              currentPreview.currentDefinitionVersionCode
                ? "Version " + currentPreview.currentDefinitionVersionCode
                : "No current version was reported"
            }}</strong
            ><span *ngIf="currentPreview.effectiveEndUtc">
              · until {{ formatDateTime(currentPreview.effectiveEndUtc) }} (end
              excluded)</span
            >
          </p>
          <small
            >Checked {{ formatDateTime(currentPreview.asOfUtc) }} ·
            {{ localTimezone }} · server report</small
          >
        </div>
      </ng-container>
      <ul
        *ngIf="!currentVersion.isExecutable && review.reasons.length"
        class="lifecycle-actions__availability-reasons"
        data-testid="workflow-version-executability-reasons"
      >
        <li *ngFor="let reason of review.reasons">{{ reason }}</li>
      </ul>
      <details class="lifecycle-actions__publication-details">
        <summary>Publication and process support</summary>
        <dl class="lifecycle-actions__availability-facts">
          <div>
            <dt>Publication</dt>
            <dd>
              <strong>{{ review.publication.answer }}</strong>
              <p>{{ review.publication.detail }}</p>
            </dd>
          </div>
          <div>
            <dt>Process support</dt>
            <dd>
              <strong>{{ review.runtime.answer }}</strong>
              <p>{{ review.runtime.detail }}</p>
            </dd>
          </div>
        </dl>
      </details>
    </section>

    <div
      *ngIf="canActivate() || canRollback()"
      class="lifecycle-actions__action-choices"
      aria-label="Choose a version action"
    >
      <div
        *ngIf="canActivate() && checkedPreview?.actionCode === 'make_current'"
      >
        <ui-button
          controlId="workflow-make-current"
          class="lifecycle-actions__primary"
          severity="primary"
          [disabled]="!canMakeCurrent()"
          (click)="makeCurrentVersion(view)"
        >
          {{ isBusy() ? "Saving…" : "Make current version" }}
        </ui-button>
        <p>
          New runs will use this version. Existing runs keep their current
          version.
        </p>
      </div>
      <div
        *ngIf="
          canActivate() &&
          !currentVersion.isExecutable &&
          checkedPreview?.actionCode === 'activate'
        "
      >
        <ui-button
          controlId="workflow-activate-now"
          class="lifecycle-actions__primary"
          severity="primary"
          [disabled]="!canActivateNow()"
          (click)="activateNow(view)"
          >{{ isBusy() ? "Saving…" : "Activate now" }}</ui-button
        >
        <p>Start using this version for new process runs.</p>
      </div>
      <div
        *ngIf="canRollback() && checkedPreview?.actionCode === 'rollback'"
        class="lifecycle-actions__rollback-choice"
      >
        <ui-button
          class="lifecycle-actions__primary"
          severity="primary"
          [outlined]="false"
          [disabled]="isBusy() || isConfirming() || interactionBlocked"
          [ariaExpanded]="versionAction() === 'rollback'"
          ariaControls="workflow-rollback-schedule"
          (click)="selectVersionAction('rollback', view)"
        >
          Roll back to this version
        </ui-button>
        <p>
          Replace an existing selection with this version for a specified
          window.
        </p>
      </div>
      <details
      *ngIf="
        canActivate() &&
        !currentVersion.isExecutable &&
        (checkedPreview?.actionCode === 'activate' ||
          checkedPreview?.actionCode === 'make_current' ||
          !!activationReasonCode)
      "
      class="lifecycle-actions__publication-details"
      [open]="!!activationReasonCode"
    >
        <summary>Add an activation note <small>Optional</small></summary>
        <label>
          <span>Activation note</span>
          <input
            uiControl
            type="text"
            name="activationImmediateNote"
            maxlength="120"
            autocomplete="off"
            placeholder="For example: Ready for the team to test"
            [(ngModel)]="activationReasonCode"
            [disabled]="isBusy() || isConfirming() || interactionBlocked"
          />
          <small
            >A short note for the history, in your own words. You can leave this
            blank.</small
          >
        </label>
      </details>
    </div>

    <details
      *ngIf="canActivate() || canRollback()"
      class="lifecycle-actions__scheduling"
      [open]="versionAction() !== null"
      data-availability-scheduling
    >
      <summary>Scheduling</summary>
      <div *ngIf="canActivate()">
        <ui-button
          class="lifecycle-actions__secondary"
          severity="secondary"
          [outlined]="true"
          [disabled]="isBusy() || isConfirming() || interactionBlocked"
          [ariaExpanded]="versionAction() === 'activate'"
          ariaControls="workflow-activation-schedule"
          (click)="selectVersionAction('activate', view)"
        >
          Schedule activation
        </ui-button>
        <p>Choose a start date and an optional end date.</p>
      </div>

    <div
      *ngIf="canActivate() || canRollback()"
      [hidden]="!versionAction()"
      class="lifecycle-actions__schedules"
    >
      <form
        *ngIf="canActivate()"
        id="workflow-activation-schedule"
        [hidden]="versionAction() !== 'activate'"
        #activationScheduleForm
        class="lifecycle-actions__card"
        (ngSubmit)="activateVersion(activationScheduleForm, view)"
      >
        <div>
          <h5>Schedule activation</h5>
          <p>{{ versionActionContext() }}</p>
        </div>
        <p
          id="workflow-activation-timezone"
          class="lifecycle-actions__timezone"
        >
          Enter dates in your local timezone:
          <strong>{{ localTimezone }}</strong
          >. Availability and conflicting schedules are checked before saving.
        </p>
        <fieldset
          class="lifecycle-actions__fields lifecycle-actions__fields--schedule"
          [disabled]="isBusy() || isConfirming() || interactionBlocked"
        >
          <legend class="visually-hidden">Activation window and reason</legend>
          <label>
            <span>Starts at</span>
            <input
              uiControl
              #activationStartInput
              type="datetime-local"
              step="any"
              name="activationStart"
              required
              [(ngModel)]="activationStart"
              aria-describedby="workflow-activation-timezone workflow-activation-start-feedback"
              [attr.aria-invalid]="
                activationStartInput.validity.badInput ||
                (activationStart && activationSchedule().startError)
                  ? 'true'
                  : null
              "
            />
            <small
              id="workflow-activation-start-feedback"
              [class.lifecycle-actions__field-error]="
                activationStartInput.validity.badInput ||
                (activationStart && activationSchedule().startError)
              "
            >
              {{
                activationStartInput.validity.badInput
                  ? "Finish entering a valid start date and time."
                  : activationSchedule().startError ||
                    "The start time is included in this window."
              }}
            </small>
          </label>
          <label>
            <span>Ends at <small>Optional</small></span>
            <input
              uiControl
              #activationEndInput
              type="datetime-local"
              step="any"
              name="activationEnd"
              [(ngModel)]="activationEnd"
              aria-describedby="workflow-activation-timezone workflow-activation-end-feedback"
              [attr.aria-invalid]="
                activationEndInput.validity.badInput ||
                activationSchedule().endError
                  ? 'true'
                  : null
              "
            />
            <small
              id="workflow-activation-end-feedback"
              [class.lifecycle-actions__field-error]="
                activationEndInput.validity.badInput ||
                activationSchedule().endError
              "
            >
              {{
                activationEndInput.validity.badInput
                  ? "Finish entering a valid end date and time, or clear the entire field for no scheduled end."
                  : activationSchedule().endError ||
                    "Leave blank for no scheduled end. The end time is excluded and must still be in the future when saved."
              }}
            </small>
          </label>
          <label>
            <span>Activation note <small>Optional</small></span>
            <input
              uiControl
              type="text"
              name="activationReasonCode"
              maxlength="120"
              autocomplete="off"
              placeholder="For example: Launch after staff training"
              aria-describedby="workflow-activation-reason-hint"
              [(ngModel)]="activationReasonCode"
            />
            <small id="workflow-activation-reason-hint"
              >A short note for the history, in your own words.</small
            >
          </label>
        </fieldset>
        <p class="lifecycle-actions__schedule-preview" aria-live="polite">
          {{
            activationStartInput.validity.badInput ||
            activationEndInput.validity.badInput
              ? "Finish the incomplete date and time before reviewing this action."
              : schedulePreview("activate")
          }}
        </p>
        <ui-button
          buttonType="submit"
          class="lifecycle-actions__primary"
          severity="primary"
          [disabled]="
            !canSubmitActivation() ||
            activationStartInput.validity.badInput ||
            activationEndInput.validity.badInput
          "
        >
          {{ isBusy() ? "Saving…" : "Schedule activation" }}
        </ui-button>
      </form>

      <form
        *ngIf="canRollback()"
        id="workflow-rollback-schedule"
        [hidden]="versionAction() !== 'rollback'"
        #rollbackScheduleForm
        class="lifecycle-actions__card"
        (ngSubmit)="rollbackToVersion(rollbackScheduleForm, view)"
      >
        <div>
          <h5>Rollback schedule</h5>
          <p>{{ versionActionContext() }}</p>
        </div>
        <p id="workflow-rollback-timezone" class="lifecycle-actions__timezone">
          Enter dates in your local timezone:
          <strong>{{ localTimezone }}</strong
          >. Rollback requires an existing selection covering the requested
          window. The server checks coverage and conflicts; existing workflow
          instances keep their version.
        </p>
        <fieldset
          class="lifecycle-actions__fields lifecycle-actions__fields--schedule"
          [disabled]="isBusy() || isConfirming() || interactionBlocked"
        >
          <legend class="visually-hidden">Rollback window and reason</legend>
          <label>
            <span>Starts at <small>Optional</small></span>
            <input
              uiControl
              #rollbackStartInput
              type="datetime-local"
              step="any"
              name="rollbackStart"
              [(ngModel)]="rollbackStart"
              aria-describedby="workflow-rollback-timezone workflow-rollback-start-feedback"
              [attr.aria-invalid]="
                rollbackStartInput.validity.badInput ||
                rollbackSchedule().startError
                  ? 'true'
                  : null
              "
            />
            <small
              id="workflow-rollback-start-feedback"
              [class.lifecycle-actions__field-error]="
                rollbackStartInput.validity.badInput ||
                rollbackSchedule().startError
              "
            >
              {{
                rollbackStartInput.validity.badInput
                  ? "Finish entering a valid start date and time, or clear the entire field to use the server’s processing time."
                  : rollbackSchedule().startError ||
                    "Leave blank to start when the server processes the rollback. A supplied start must still be in the future when saved."
              }}
            </small>
          </label>
          <label>
            <span>Ends at <small>Optional</small></span>
            <input
              uiControl
              #rollbackEndInput
              type="datetime-local"
              step="any"
              name="rollbackEnd"
              [(ngModel)]="rollbackEnd"
              aria-describedby="workflow-rollback-timezone workflow-rollback-end-feedback"
              [attr.aria-invalid]="
                rollbackEndInput.validity.badInput ||
                rollbackSchedule().endError
                  ? 'true'
                  : null
              "
            />
            <small
              id="workflow-rollback-end-feedback"
              [class.lifecycle-actions__field-error]="
                rollbackEndInput.validity.badInput ||
                rollbackSchedule().endError
              "
            >
              {{
                rollbackEndInput.validity.badInput
                  ? "Finish entering a valid end date and time, or clear the entire field for no scheduled end."
                  : rollbackSchedule().endError ||
                    "Leave blank for no scheduled end. A supplied end must still be in the future when saved."
              }}
            </small>
          </label>
          <label>
            <span>Rollback note <small>Optional</small></span>
            <input
              uiControl
              type="text"
              name="rollbackReasonCode"
              maxlength="120"
              autocomplete="off"
              placeholder="For example: Restore the previous process"
              aria-describedby="workflow-rollback-reason-hint"
              [(ngModel)]="rollbackReasonCode"
            />
            <small id="workflow-rollback-reason-hint"
              >A short note for the history, in your own words.</small
            >
          </label>
        </fieldset>
        <p class="lifecycle-actions__schedule-preview" aria-live="polite">
          {{
            rollbackStartInput.validity.badInput ||
            rollbackEndInput.validity.badInput
              ? "Finish the incomplete date and time before reviewing this action."
              : schedulePreview("rollback")
          }}
        </p>
        <ui-button
          buttonType="submit"
          class="lifecycle-actions__secondary"
          severity="secondary"
          [outlined]="true"
          [disabled]="
            !canSubmitRollback() ||
            rollbackStartInput.validity.badInput ||
            rollbackEndInput.validity.badInput
          "
        >
          {{ isBusy() ? "Saving…" : "Review rollback" }}
        </ui-button>
      </form>
    </div>

    </details>

    <details
      class="lifecycle-actions__activations"
      [open]="isLoadingActivations() || activationsLoadFailed() || retiringActivationId() !== null"
      data-availability-history
    >
      <summary id="workflow-activations-title">
        Activation history
        <span
          >{{ activationTotalCount() }}
          {{ activationTotalCount() === 1 ? "record" : "records" }}</span
        >
      </summary>
      <p>Windows for this version only. Other versions may also have activation windows.</p>

      <p
        *ngIf="isLoadingActivations()"
        class="lifecycle-actions__permission-note"
        role="status"
      >
        Loading activation history…
      </p>
      <div
        *ngIf="activationsLoadFailed()"
        class="lifecycle-actions__permission-note"
      >
        <p>
          Activation history could not be loaded. Availability is not inferred
          from missing history.
        </p>
        <ui-button
          class="lifecycle-actions__secondary"
          severity="secondary"
          [outlined]="true"
          [disabled]="isBusy() || isLoadingActivations()"
          (click)="retryActivationHistory(view)"
          >Retry history</ui-button
        >
      </div>
      <p
        *ngIf="
          !isLoadingActivations() &&
          !activationsLoadFailed() &&
          activations().length === 0
        "
        class="lifecycle-actions__permission-note"
      >
        No activation records were returned for this version.
      </p>
      <p
        *ngIf="activationTotalCount() > activations().length"
        class="lifecycle-actions__permission-note"
      >
        Showing {{ activations().length }} of
        {{ activationTotalCount() }} records.
      </p>
      <p
        *ngIf="activations().length > 0"
        class="lifecycle-actions__permission-note"
      >
        Times shown in {{ localTimezone }}. Status is reported by the server at
        each record’s evaluation time.
      </p>
      <ul
        *ngIf="activations().length > 0"
        class="lifecycle-actions__activation-list"
      >
        <li *ngFor="let activation of activations()">
          <div class="lifecycle-actions__activation-summary">
            <strong>{{ activationPostureLabel(activation) }}</strong>
            <span
              >{{ formatDateTime(activation.effectiveStartUtc) }} —
              {{
                activation.effectiveEndUtc
                  ? formatDateTime(activation.effectiveEndUtc) +
                    " (end excluded)"
                  : "No scheduled end"
              }}</span
            >
            <details class="lifecycle-actions__record-details">
              <summary>Record details</summary>
              <dl>
                <div>
                  <dt>Activation ID</dt>
                  <dd>{{ activation.id }}</dd>
                </div>
                <div>
                  <dt>Stored lifecycle</dt>
                  <dd>
                    <code>{{ activation.lifecycleStatusCode }}</code>
                  </dd>
                </div>
                <div>
                  <dt>Reported status</dt>
                  <dd>
                    <code>{{ activation.activationPostureCode }}</code>
                  </dd>
                </div>
                <div>
                  <dt>Evaluated at</dt>
                  <dd>{{ formatDateTime(activation.asOfUtc) }}</dd>
                </div>
                <div>
                  <dt>Exact start (UTC)</dt>
                  <dd>
                    <code>{{ activation.effectiveStartUtc }}</code>
                  </dd>
                </div>
                <div>
                  <dt>Exact end (UTC)</dt>
                  <dd>
                    <code>{{
                      activation.effectiveEndUtc || "No scheduled end"
                    }}</code>
                  </dd>
                </div>
                <div>
                  <dt>Note</dt>
                  <dd>{{ activation.reasonCode || "Not recorded" }}</dd>
                </div>
              </dl>
            </details>
          </div>
          <div
            *ngIf="canRetire() && activation.lifecycleStatusCode !== 'retired'"
            class="lifecycle-actions__retirement"
          >
            <ui-button
              class="lifecycle-actions__secondary"
              severity="secondary"
              [outlined]="true"
              [disabled]="isBusy() || isConfirming() || interactionBlocked"
              [ariaExpanded]="retiringActivationId() === activation.id"
              [ariaControls]="'workflow-retire-' + activation.id"
              [ariaLabel]="
                'Retire activation ' +
                activation.id +
                ' beginning ' +
                formatDateTime(activation.effectiveStartUtc)
              "
              (click)="selectRetirement(activation, view)"
              >Retire this window</ui-button
            >
            <form
              #retirementForm
              [id]="'workflow-retire-' + activation.id"
              [hidden]="retiringActivationId() !== activation.id"
              class="lifecycle-actions__retire-form"
              (ngSubmit)="retireActivation(activation, view, retirementForm)"
            >
              <p>
                Remove this window from selection for new work. Existing
                workflow instances keep their version.
              </p>
              <label>
                <span>Retirement note <small>Optional</small></span>
                <input
                  uiControl
                  type="text"
                  [name]="'retireReason' + activation.id"
                  maxlength="120"
                  autocomplete="off"
                  placeholder="For example: This schedule is no longer needed"
                  [disabled]="isBusy() || isConfirming() || interactionBlocked"
                  [(ngModel)]="retireReasonCodes[activation.id]"
                />
              </label>
              <ui-button
                buttonType="submit"
                class="lifecycle-actions__danger"
                severity="danger"
                [outlined]="true"
                [disabled]="
                  isBusy() ||
                  isConfirming() ||
                  interactionBlocked ||
                  (retireReasonCodes[activation.id] || '').trim().length > 120
                "
              >
                Review retirement
              </ui-button>
            </form>
          </div>
        </li>
      </ul>
    </details>
  </ng-container>
</section>
